import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const PwdInput = ({ id, value, onChange, autoComplete, visible, onToggle, testId }) => (
  <div className="relative">
    <Input
      id={id}
      type={visible ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      className="pr-10"
      data-testid={testId}
    />
    <button
      type="button"
      onClick={onToggle}
      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 focus:outline-none focus:text-slate-700"
      aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      data-testid={`${testId}-toggle`}
      tabIndex={-1}
    >
      {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  </div>
);

const MiCuentaPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const [profile, setProfile] = useState({
    nombre: user?.nombre || '',
    email: user?.email || '',
    current_password: '',
  });

  const [pwd, setPwd] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [showPwd, setShowPwd] = useState({
    current: false,
    next: false,
    confirm: false,
  });

  const togglePwd = (key) => setShowPwd((s) => ({ ...s, [key]: !s[key] }));


  const emailChanged = profile.email.trim().toLowerCase() !== (user?.email || '').toLowerCase();
  const nombreChanged = profile.nombre.trim() !== (user?.nombre || '');

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!nombreChanged && !emailChanged) {
      toast.info('No hay cambios para guardar');
      return;
    }
    if (emailChanged && !profile.current_password) {
      toast.error('Para cambiar el email ingresá tu contraseña actual');
      return;
    }
    setSavingProfile(true);
    try {
      const body = {};
      if (nombreChanged) body.nombre = profile.nombre.trim();
      if (emailChanged) {
        body.email = profile.email.trim().toLowerCase();
        body.current_password = profile.current_password;
      }
      await authApi.updateMe(body);
      if (emailChanged) {
        toast.success('Email actualizado. Iniciá sesión con tu nuevo email.');
        await logout();
        navigate('/login');
      } else {
        toast.success('Perfil actualizado');
        setProfile((p) => ({ ...p, current_password: '' }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error actualizando perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwd.current_password || !pwd.new_password) {
      toast.error('Completá todos los campos');
      return;
    }
    if (pwd.new_password.length < 8) {
      toast.error('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (pwd.new_password !== pwd.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setSavingPwd(true);
    try {
      await authApi.updateMe({
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success('Contraseña cambiada. Iniciá sesión de nuevo.');
      await logout();
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error cambiando contraseña');
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="mi-cuenta-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <User className="w-6 h-6" /> Mi cuenta
        </h1>
        <p className="text-sm text-slate-500 mt-1">Administrá tus datos de acceso</p>
      </div>

      {/* Perfil */}
      <form onSubmit={handleSaveProfile} className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <h2 className="text-base font-semibold text-slate-900 mb-4">Datos del perfil</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-slate-500">Rol</Label>
            <div className="mt-1 px-3 py-2 bg-slate-50 rounded border border-slate-200 text-sm text-slate-700 capitalize" data-testid="mi-cuenta-rol">
              {user?.rol?.replace(/_/g, ' ')}
            </div>
          </div>
          <div>
            <Label htmlFor="nombre">Nombre</Label>
            <Input
              id="nombre"
              value={profile.nombre}
              onChange={(e) => setProfile({ ...profile, nombre: e.target.value })}
              data-testid="mi-cuenta-nombre"
            />
          </div>
          <div>
            <Label htmlFor="email" className="flex items-center gap-1">
              <Mail className="w-3.5 h-3.5" /> Email
            </Label>
            <Input
              id="email"
              type="email"
              value={profile.email}
              onChange={(e) => setProfile({ ...profile, email: e.target.value })}
              data-testid="mi-cuenta-email"
            />
            {emailChanged && (
              <p className="text-xs text-amber-700 mt-1">
                Vas a cambiar tu email de acceso. Después tendrás que iniciar sesión con el nuevo.
              </p>
            )}
          </div>
          {emailChanged && (
            <div>
              <Label htmlFor="current_pwd_email">Contraseña actual (para confirmar)</Label>
              <Input
                id="current_pwd_email"
                type="password"
                value={profile.current_password}
                onChange={(e) => setProfile({ ...profile, current_password: e.target.value })}
                autoComplete="current-password"
                data-testid="mi-cuenta-current-pwd"
              />
            </div>
          )}
          <Button
            type="submit"
            disabled={savingProfile || (!nombreChanged && !emailChanged)}
            data-testid="mi-cuenta-save-profile"
          >
            <Save className="w-4 h-4 mr-2" />
            {savingProfile ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </div>
      </form>

      {/* Contraseña */}
      <form onSubmit={handleChangePassword} className="bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="text-base font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <Lock className="w-4 h-4" /> Cambiar contraseña
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Mínimo 8 caracteres. Después de cambiarla tendrás que iniciar sesión de nuevo.
        </p>
        <div className="space-y-4">
          <div>
            <Label htmlFor="current_pwd">Contraseña actual</Label>
            <PwdInput
              id="current_pwd"
              value={pwd.current_password}
              onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
              autoComplete="current-password"
              visible={showPwd.current}
              onToggle={() => togglePwd('current')}
              testId="mi-cuenta-pwd-current"
            />
          </div>
          <div>
            <Label htmlFor="new_pwd">Nueva contraseña</Label>
            <PwdInput
              id="new_pwd"
              value={pwd.new_password}
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
              autoComplete="new-password"
              visible={showPwd.next}
              onToggle={() => togglePwd('next')}
              testId="mi-cuenta-pwd-new"
            />
          </div>
          <div>
            <Label htmlFor="confirm_pwd">Confirmar nueva contraseña</Label>
            <PwdInput
              id="confirm_pwd"
              value={pwd.confirm_password}
              onChange={(e) => setPwd({ ...pwd, confirm_password: e.target.value })}
              autoComplete="new-password"
              visible={showPwd.confirm}
              onToggle={() => togglePwd('confirm')}
              testId="mi-cuenta-pwd-confirm"
            />
          </div>
          <Button type="submit" disabled={savingPwd} data-testid="mi-cuenta-save-pwd">
            <Save className="w-4 h-4 mr-2" />
            {savingPwd ? 'Cambiando...' : 'Cambiar contraseña'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MiCuentaPage;
